import { DEFAULT_OCCUPANCY } from 'components/NumberOfGuests/constants';
import { NumberOfGuestsSelection } from 'components/NumberOfGuests/types';
import { Dispatch, ReactNode, SetStateAction, createContext, useCallback, useContext, useMemo, useState } from 'react';

export interface FiltersModalProviderProps {
	children: ReactNode;
}

export interface FiltersModalContextState {
	showFiltersModal: boolean;
	setShowFiltersModal: Dispatch<SetStateAction<boolean>>;
	numberOfGuestsSelection: NumberOfGuestsSelection;
	setNumberOfGuestsSelection: Dispatch<SetStateAction<NumberOfGuestsSelection>>;
	numberOfGuestsApplied: NumberOfGuestsSelection;
	updateNumberOfGuestsApplied: () => void;
	isDefaultOccupancySelected: boolean;
	isDefaultOccupancyApplied: boolean;
	clearNumberOfGuestsSelection: () => void;
	clearNumberOfGuestsApplied: () => void;
	resetNumberOfGuestsSelectionToApplied: () => void;
}

const FiltersModalContext = createContext<FiltersModalContextState>(undefined as unknown as FiltersModalContextState);

export const useFiltersModalContext = () => useContext(FiltersModalContext);
export function FiltersModalProvider({ children }: Readonly<FiltersModalProviderProps>) {
	const [showFiltersModal, setShowFiltersModal] = useState(false);
	const [numberOfGuestsSelection, setNumberOfGuestsSelection] = useState<NumberOfGuestsSelection>(DEFAULT_OCCUPANCY);
	const [numberOfGuestsApplied, setNumberOfGuestsApplied] = useState<NumberOfGuestsSelection>(DEFAULT_OCCUPANCY);

	const isDefaultOccupancySelected = useMemo(() => numberOfGuestsSelection === DEFAULT_OCCUPANCY, [numberOfGuestsSelection]);
	const isDefaultOccupancyApplied = useMemo(() => numberOfGuestsApplied === DEFAULT_OCCUPANCY, [numberOfGuestsApplied]);

	const updateNumberOfGuestsApplied = useCallback(() => {
		setNumberOfGuestsApplied(numberOfGuestsSelection);
	}, [setNumberOfGuestsApplied, numberOfGuestsSelection]);

	const resetNumberOfGuestsSelectionToApplied = useCallback(() => {
		setNumberOfGuestsSelection(numberOfGuestsApplied);
	}, [setNumberOfGuestsSelection, numberOfGuestsApplied]);

	const clearNumberOfGuestsSelection = useCallback(() => {
		setNumberOfGuestsSelection(DEFAULT_OCCUPANCY);
	}, [setNumberOfGuestsSelection]);

	const clearNumberOfGuestsApplied = useCallback(() => {
		setNumberOfGuestsApplied(DEFAULT_OCCUPANCY);
		clearNumberOfGuestsSelection();
	}, [setNumberOfGuestsApplied, clearNumberOfGuestsSelection]);

	const value = useMemo<FiltersModalContextState>(
		() => ({
			showFiltersModal,
			setShowFiltersModal,
			numberOfGuestsSelection,
			setNumberOfGuestsSelection,
			numberOfGuestsApplied,
			updateNumberOfGuestsApplied,
			isDefaultOccupancySelected,
			isDefaultOccupancyApplied,
			clearNumberOfGuestsSelection,
			clearNumberOfGuestsApplied,
			resetNumberOfGuestsSelectionToApplied,
		}),
		[
			showFiltersModal,
			setShowFiltersModal,
			numberOfGuestsSelection,
			setNumberOfGuestsSelection,
			numberOfGuestsApplied,
			updateNumberOfGuestsApplied,
			isDefaultOccupancySelected,
			isDefaultOccupancyApplied,
			clearNumberOfGuestsSelection,
			clearNumberOfGuestsApplied,
			resetNumberOfGuestsSelectionToApplied,
		],
	);

	return <FiltersModalContext.Provider value={value}>{children}</FiltersModalContext.Provider>;
}
